// VMS 的登入 session（plan/vms-web-v0.md §3.1、AUTH-02）：
//   - refresh token 只在 vms-web 的 HttpOnly cookie；SDK 的 session 只放記憶體（memoryPersistence）
//   - refresh / logout 改打 vms-web 的 /session/*（setAuthTransport）
//   - 重新整理頁面：POST /session/token 換一顆新的 access token 還原登入

import {
  KinioError,
  adoptAccessToken,
  adoptLogin,
  configureKinio,
  logout as sdkLogout,
  refresh as sdkRefresh,
  requestStepUp,
  memoryPersistence,
  session,
  setAuthTransport,
  setSessionPersistence,
  type KinioSession,
  type LoginResponse,
} from '@kinio/web-sdk';
import type { AppConfig } from './appConfig';
import { multiScreen } from './multiScreen';
import { clearSsoLink } from './pendingSsoLink';
import { credentialToJSON, toCreationOptions, toRequestOptions } from './webauthn';

export class SessionError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** 錯誤 body 的 `detail`（例：409 SSO_NOT_LINKED 的 reason）；沒有 → {}。 */
    readonly detail: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

type BffLogin = LoginResponse;

async function bff<T>(path: string, body?: unknown, bearer?: string | null, fetchImpl: typeof fetch = fetch, extra?: Record<string, string>): Promise<T> {
  const headers: Record<string, string> = { 'X-Kinio-CSRF': '1', ...extra };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (bearer) headers.Authorization = 'Bearer ' + bearer;
  let res: Response;
  try {
    res = await fetchImpl(path, { method: 'POST', headers, credentials: 'same-origin', body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new SessionError(0, 'NETWORK', 'network error');
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: Record<string, unknown> = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    /* 非 JSON */
  }
  if (!res.ok) {
    const detail = data.detail && typeof data.detail === 'object' && !Array.isArray(data.detail) ? (data.detail as Record<string, unknown>) : {};
    throw new SessionError(res.status, String(data.error ?? 'HTTP_' + res.status), String(data.message ?? ''), detail);
  }
  return data as T;
}

// ---- 閒置逾時（AUTH-04、§14 #15）：閒置 = 使用者沒操作。換 token 時告訴伺服器多久沒操作，identity 依 org 政策判定。----

let lastInput = Date.now();
let logoutReason: string | null = null;
let mcpBase = ''; // 遠端 HTTP MCP（plan SPEC #api-keys-mcp-remote）：mcp.<plane> 跟 id.<plane> 同網域；認不得 = ''

/** 建金鑰後給的 Claude Code 指令：遠端 HTTP MCP（不用裝程式）；名稱 kinio-vms（develop portal 的是 kinio-portal）。認不得 plane = ''。 */
export function remoteMcpSetup(secret: string): string {
  return mcpBase ? `claude mcp add --transport http kinio-vms ${mcpBase} --header "Authorization: Bearer ${secret}"` : '';
}

/** 記錄使用者操作（滑鼠 / 鍵盤 / 觸控 / 捲動）；啟動時呼叫一次。 */
export function trackActivity(target: Pick<Window, 'addEventListener'> = window): void {
  const mark = () => {
    lastInput = Date.now();
    multiScreen.activity(lastInput); // LIVE-10：其他視窗也看得到這次操作（最多每 30 秒一次）
  };
  for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove']) target.addEventListener(ev, mark, { passive: true, capture: true });
}

/** 別的 VMS 視窗有操作（LIVE-10 多螢幕：閒置看所有視窗的最後活動）。 */
export function noteActivity(at: number): void {
  if (at > lastInput && at <= Date.now() + 5_000) lastInput = at;
}

/** 距離上次操作幾秒。 */
export function idleSeconds(now = Date.now()): number {
  return Math.max(0, Math.floor((now - lastInput) / 1000));
}

/** 上一次被登出的原因（例如閒置逾時），登入頁顯示後清掉。 */
export function takeLogoutReason(): string | null {
  const r = logoutReason;
  logoutReason = null;
  return r;
}

/** 啟動時呼叫一次：注入 SDK 設定、改成記憶體 session + BFF 傳輸。 */
export function initSession(cfg: AppConfig, fetchImpl: typeof fetch = fetch): void {
  configureKinio({ brandId: cfg.brand, brandName: cfg.display_name, identityBase: cfg.identity_base });
  const m = /^https:\/\/id\.([a-z0-9.-]+)\/?$/i.exec(cfg.identity_base ?? '');
  mcpBase = m ? `https://mcp.${m[1].toLowerCase()}/vms` : ''; // VMS 的 MCP（develop portal 的是 /portal；SPEC #api-keys-split）
  setSessionPersistence(memoryPersistence);
  setAuthTransport({
    refresh: async () => {
      try {
        return await bff<BffLogin>('/session/token', undefined, null, fetchImpl, { 'X-Kinio-Idle-Seconds': String(idleSeconds()) });
      } catch (e) {
        if (e instanceof SessionError && (e.code === 'SESSION_IDLE' || e.code === 'SESSION_REAUTH')) logoutReason = e.code;
        // SDK 只認 KinioError 的狀態碼來決定要不要清 session
        if (e instanceof SessionError) throw new KinioError(e.status, e.code, e.message);
        throw e;
      }
    },
    logout: async (cur: KinioSession) => {
      await bff<void>('/session/logout', undefined, cur.access_token, fetchImpl);
    },
  });
}

/** 登入（只有一種帳號；client 由 vms-web 依品牌決定，SPEC AUTH-01）。 */
export async function login(email: string, password: string, fetchImpl: typeof fetch = fetch): Promise<KinioSession> {
  const r = await bff<BffLogin>('/session/login', { email: email.trim(), password }, null, fetchImpl);
  return adoptLogin(r);
}

/** 註冊（AUTH-01：沒有帳號可註冊；同一個品牌 client、同一個帳號池）。受邀的人走接受邀請頁（/v1/orgs/join/register）。 */
export async function signup(email: string, password: string, displayName = '', fetchImpl: typeof fetch = fetch): Promise<KinioSession> {
  const r = await bff<BffLogin>('/session/signup', { email: email.trim(), password, display_name: displayName.trim() }, null, fetchImpl);
  return adoptLogin(r);
}

/** 忘記密碼：帳號存不存在都一樣回 202（不洩漏）；重設信依這個網址的 VMS client 帶品牌。 */
export async function passwordForgot(email: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  await bff<unknown>('/session/password/forgot', { email: email.trim() }, null, fetchImpl);
}

// ---- 兩步驟驗證（plan/vms-web-v0.md AUTH-03）----
// login() 丟 SessionError 'MFA_REQUIRED' / 'MFA_ENROLL_REQUIRED' 時，mfa_token 已由 vms-web 收進 HttpOnly cookie，
// 這裡只要送驗證碼。

/** 登入第二步：驗證碼（或備援碼）。 */
export async function loginWithCode(code: string, fetchImpl: typeof fetch = fetch): Promise<KinioSession> {
  const r = await bff<BffLogin>('/session/mfa', { code: code.trim() }, null, fetchImpl);
  return adoptLogin(r);
}

export interface TotpSetup {
  secret: string;
  otpauth_uri: string;
}

/** 設定 TOTP：登入中（identity 要求先設定時）不帶 bearer；設定頁帶目前的 access token。 */
export function totpSetup(bearer: string | null = null, fetchImpl: typeof fetch = fetch): Promise<TotpSetup> {
  return bff<TotpSetup>('/session/mfa/setup', {}, bearer, fetchImpl);
}

export type EnrollResult = BffLogin & { recovery_codes: string[] };

/** 登入中設定完成：回 token + 備援碼，但先不建立 session（讓使用者先抄下備援碼，再 finishEnroll）。 */
export function totpConfirmDuringLogin(code: string, fetchImpl: typeof fetch = fetch): Promise<EnrollResult> {
  return bff<EnrollResult>('/session/mfa/confirm', { code: code.trim() }, null, fetchImpl);
}

export function finishEnroll(r: EnrollResult): KinioSession {
  return adoptLogin(r);
}

/**
 * 設定頁：開始設定 TOTP。identity 的 setup / confirm 都要 5 分鐘內驗證過（AUTH-05）：
 * 回 REAUTH_REQUIRED 就跳重新驗證、用新的 token 再送一次（不用使用者關掉對話框重來）。
 */
export function totpSetupSignedIn(fetchImpl: typeof fetch = fetch): Promise<TotpSetup> {
  return withStepUp(() => bff<TotpSetup>('/session/mfa/setup', {}, session.accessToken(), fetchImpl));
}

/** 設定頁：確認 TOTP → 備援碼。掃 QR 花超過 5 分鐘時 identity 回 REAUTH_REQUIRED → 重新驗證後用同一個驗證碼重送（待確認的 secret 還在）。 */
export function totpConfirm(code: string, fetchImpl: typeof fetch = fetch): Promise<{ recovery_codes: string[] }> {
  return withStepUp(() => bff('/session/mfa/confirm', { code: code.trim() }, session.accessToken(), fetchImpl));
}

export interface MfaStatus {
  totp_enabled: boolean;
  totp_enabled_at?: string;
  recovery_remaining: number;
  required: boolean;
}

export function mfaStatus(fetchImpl: typeof fetch = fetch): Promise<MfaStatus> {
  return bff<MfaStatus>('/session/mfa/status', {}, session.accessToken(), fetchImpl);
}

export function mfaDisable(password: string, code: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  return bff('/session/mfa/disable', { password, code: code.trim() }, session.accessToken(), fetchImpl);
}

export function regenerateRecoveryCodes(code: string, fetchImpl: typeof fetch = fetch): Promise<{ recovery_codes: string[] }> {
  return bff('/session/mfa/recovery', { code: code.trim() }, session.accessToken(), fetchImpl);
}

/** 重新驗證（AUTH-05）：成功後換上新的 access token（auth_time 更新；refresh 不變）。 */
export async function reauth(password: string, code: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  const send = () => bff<{ access_token: string; expires_in?: number }>('/session/reauth', { password, code: code.trim() }, session.accessToken(), fetchImpl);
  let r: { access_token: string; expires_in?: number };
  try {
    r = await send();
  } catch (e) {
    // 別的分頁 refresh 過 → 這顆 access token 的 session 已輪換：換一顆新的再送一次（identity 先查 session，驗證碼還沒被用掉）
    if (!(e instanceof SessionError && e.code === 'SESSION_UNKNOWN')) throw e;
    await sdkRefresh();
    r = await send();
  }
  adoptAccessToken(r.access_token, r.expires_in);
}

// ---- passkey（AUTH-03、§14 #14）：無密碼登入、本身算兩步驟驗證 ----

type Json = Record<string, unknown>;

async function webauthnGet(options: Json): Promise<Json> {
  const cred = (await navigator.credentials.get({ publicKey: toRequestOptions(options) })) as PublicKeyCredential | null;
  if (!cred) throw new SessionError(0, 'PASSKEY_CANCELED', '');
  return credentialToJSON(cred);
}

/** 用 passkey 登入（state 由 vms-web 收在 HttpOnly cookie）。 */
export async function passkeyLogin(fetchImpl: typeof fetch = fetch): Promise<KinioSession> {
  const begin = await bff<{ options: Json }>('/session/passkey/login/begin', {}, null, fetchImpl);
  const credential = await webauthnGet(begin.options);
  const r = await bff<BffLogin>('/session/passkey/login/finish', { credential }, null, fetchImpl);
  return adoptLogin(r);
}

export interface PasskeyInfo {
  id: string;
  name: string;
  rp_id: string;
  created_at: string;
  last_used_at?: string;
  backup_eligible: boolean;
  /** 在 Kinio KMS 簽章網址註冊的（rp_id = plane 網域，D35）：只能簽 IT 監控的政策與管理員清單，不能登入。 */
  kms_sign?: boolean;
}

/** 走 BFF 的敏感操作：REAUTH_REQUIRED → 跳重新驗證 → 再送一次。 */
async function withStepUp<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (e instanceof SessionError && e.code === 'REAUTH_REQUIRED' && (await requestStepUp())) return fn();
    throw e;
  }
}

/** 這次登入 token 的 sso_scope：SSO 登入的工作階段只能用那個組織（plan SPEC #vms-web-sso-flows「SSO 登入的範圍」）。沒有 = 空字串。 */
export function ssoScope(token: string | null = session.accessToken()): string {
  try {
    const p = JSON.parse(atob((token ?? '').split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof p.sso_scope === 'string' ? p.sso_scope : '';
  } catch {
    return '';
  }
}

/** SSO-18：SSO 建的新帳號、還沒有自己的登入方式，而且這次是 SSO 登入（有範圍）→ 要先設第一把 passkey。設好並重新驗證後範圍解除就不再是。 */
export function needsFirstFactor(user: unknown, token: string | null = session.accessToken()): boolean {
  return (user as { needs_first_factor?: boolean } | null)?.needs_first_factor === true && ssoScope(token) !== '';
}

/**
 * SSO-18 的第一把 passkey：註冊（identity 只對 SSO 建、還沒有因子的帳號在 SSO 範圍內放行）→ 用它重新驗證，範圍解除。
 * 不走一般的重新驗證對話框（他沒有密碼也沒有 passkey）：403 REAUTH_REQUIRED 直接丟給畫面，由畫面帶他走一次公司帳號驗證再回來。
 */
export async function setupFirstPasskey(name: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  const begin = await bff<{ options: Json; state: string }>('/session/passkey/register/begin', {}, session.accessToken(), fetchImpl);
  const cred = (await navigator.credentials.create({ publicKey: toCreationOptions(begin.options) })) as PublicKeyCredential | null;
  if (!cred) throw new SessionError(0, 'PASSKEY_CANCELED', '');
  await bff('/session/passkey/register/finish', { state: begin.state, credential: credentialToJSON(cred), name }, session.accessToken(), fetchImpl);
  await reauthWithPasskey(fetchImpl);
}

/** 新增一把 passkey（要 5 分鐘內驗證過，會自動跳重新驗證）。 */
export async function registerPasskey(name: string, fetchImpl: typeof fetch = fetch): Promise<PasskeyInfo> {
  const begin = await withStepUp(() => bff<{ options: Json; state: string }>('/session/passkey/register/begin', {}, session.accessToken(), fetchImpl));
  const cred = (await navigator.credentials.create({ publicKey: toCreationOptions(begin.options) })) as PublicKeyCredential | null;
  if (!cred) throw new SessionError(0, 'PASSKEY_CANCELED', '');
  const r = await bff<{ passkey: PasskeyInfo }>('/session/passkey/register/finish', { state: begin.state, credential: credentialToJSON(cred), name }, session.accessToken(), fetchImpl);
  return r.passkey;
}

/** 註冊 Kinio KMS 簽章用 passkey（SPEC #kinio-kms-security「簽章網址」、D35）：BFF 代打 identity begin（origin = 簽章網址），
 *  create 在簽章頁（另一個 origin）做——createOnSignPage 開簽章視窗、回 credential JSON；再經 BFF finish。要 5 分鐘內驗證過。 */
export async function registerSignPasskey(name: string, createOnSignPage: (options: Json) => Promise<Json>, fetchImpl: typeof fetch = fetch): Promise<PasskeyInfo> {
  const begin = await withStepUp(() => bff<{ options: Json; state: string }>('/session/sign-passkey/register/begin', {}, session.accessToken(), fetchImpl));
  const credential = await createOnSignPage(begin.options);
  const r = await bff<{ passkey: PasskeyInfo }>('/session/sign-passkey/register/finish', { state: begin.state, credential, name }, session.accessToken(), fetchImpl);
  return r.passkey;
}

export async function listPasskeys(fetchImpl: typeof fetch = fetch): Promise<PasskeyInfo[]> {
  const r = await bff<{ passkeys: PasskeyInfo[] }>('/session/passkeys', {}, session.accessToken(), fetchImpl);
  return r.passkeys ?? [];
}

export function deletePasskey(id: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  return withStepUp(() => bff('/session/passkeys/delete', { id }, session.accessToken(), fetchImpl));
}

/** 用 passkey 重新驗證（AUTH-05）。 */
export async function reauthWithPasskey(fetchImpl: typeof fetch = fetch): Promise<void> {
  const run = async () => {
    const begin = await bff<{ options: Json; state: string }>('/session/reauth/passkey/begin', {}, session.accessToken(), fetchImpl);
    const credential = await webauthnGet(begin.options);
    return bff<{ access_token: string; expires_in?: number }>('/session/reauth/passkey/finish', { state: begin.state, credential }, session.accessToken(), fetchImpl);
  };
  let r: { access_token: string; expires_in?: number };
  try {
    r = await run();
  } catch (e) {
    if (!(e instanceof SessionError && e.code === 'SESSION_UNKNOWN')) throw e;
    await sdkRefresh();
    r = await run();
  }
  adoptAccessToken(r.access_token, r.expires_in);
}

/**
 * 遠端協助的 step-up 收據（SPEC #kinio-kms-remote-receipt；k1-wbs M4-06）：對 kmgr 回的 session 票雜湊（ticket_hash）做 passkey 重新驗證，
 * identity 驗過後另簽 kmgr_receipt（aud kinio-kmgr-agent，≤ 2 分鐘）；同時照一般重新驗證換新的登入 token（auth_time / amr 更新）。
 * 只能用 passkey（密碼 + 驗證碼不算，Agent 要 amr 含 hwk）。
 */
export async function kmgrRemoteReceipt(sth: string, fetchImpl: typeof fetch = fetch): Promise<string> {
  const run = async () => {
    const begin = await bff<{ options: Json; state: string }>('/session/kmgr-receipt/begin', { kmgr_sth: sth }, session.accessToken(), fetchImpl);
    const credential = await webauthnGet(begin.options);
    return bff<{ access_token: string; expires_in?: number; kmgr_receipt?: string }>('/session/kmgr-receipt/finish', { state: begin.state, credential }, session.accessToken(), fetchImpl);
  };
  let r: { access_token: string; expires_in?: number; kmgr_receipt?: string };
  try {
    r = await run();
  } catch (e) {
    if (!(e instanceof SessionError && e.code === 'SESSION_UNKNOWN')) throw e;
    await sdkRefresh();
    r = await run();
  }
  adoptAccessToken(r.access_token, r.expires_in);
  if (!r.kmgr_receipt) throw new SessionError(502, 'RECEIPT_MISSING', 'identity 沒有回收據');
  return r.kmgr_receipt;
}

/** WebAuthn 例外（使用者取消、逾時）轉成一致的錯誤碼。 */
export function passkeyErrorCode(e: unknown): string {
  if (e instanceof SessionError) return e.code;
  const name = (e as { name?: string })?.name;
  if (name === 'NotAllowedError' || name === 'AbortError') return 'PASSKEY_CANCELED';
  if (name === 'InvalidStateError') return 'PASSKEY_EXISTS';
  if (name === 'SecurityError') return 'BAD_RP';
  return 'default';
}

// ---- 工作階段管理（AUTH-06）----

export interface SessionInfo {
  session_id: string;
  client_id: string;
  device_label?: string;
  last_ip?: string;
  auth_time?: string;
  last_active_at: string;
  expires_at: string;
  amr: string[];
  current: boolean;
}

export async function listSessions(fetchImpl: typeof fetch = fetch): Promise<SessionInfo[]> {
  const r = await bff<{ sessions: SessionInfo[] }>('/session/sessions', {}, session.accessToken(), fetchImpl);
  return r.sessions ?? [];
}

/** 強制登出一個工作階段；撤的是目前這個 → 本機也登出。 */
export async function revokeSession(id: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  const r = await bff<{ current?: boolean }>('/session/sessions/revoke', { session_id: id }, session.accessToken(), fetchImpl);
  if (r?.current) session.clear();
}

export async function revokeAllSessions(keepCurrent = true, fetchImpl: typeof fetch = fetch): Promise<number> {
  const r = await bff<{ revoked: number }>('/session/sessions/revoke-all', { keep_current: keepCurrent }, session.accessToken(), fetchImpl);
  if (!keepCurrent) session.clear();
  return r?.revoked ?? 0;
}

/** 重新整理後還原：cookie 還有效就換新 access token；沒有 → null。 */
export async function restore(fetchImpl: typeof fetch = fetch): Promise<KinioSession | null> {
  try {
    const r = await bff<BffLogin>('/session/token', undefined, null, fetchImpl);
    return adoptLogin(r);
  } catch (e) {
    if (e instanceof SessionError && (e.status === 401 || e.status === 403)) return null;
    throw e;
  }
}

export async function logout(): Promise<void> {
  clearSsoLink(); // 共用電腦：下一個人登入不要看到這個人的待連結組織（審查 P2-2）
  await sdkLogout();
}

export { session };

// ---- 個人 API 金鑰（plan SPEC #api-keys、AUTH-11）----

export interface ApiKeyInfo {
  key_id: string;
  name: string;
  prefix: string;
  scopes: string[];
  restrictions?: {
    org_ids?: string[];
    node_ids?: string[];
    home_ids?: string[];
  };
  created_at: string;
  expires_at: string;
  last_used_at?: string;
  last_used_ip?: string;
  last_agent?: string;
  revoked_at?: string;
  revoked_reason?: string;
  /** 輪換後的新金鑰；有值 = 已輪換，舊的照 expires_at（≤ 輪換 + 24 小時）失效 */
  replaced_by?: string;
}

/** VMS（App 帳號）金鑰的範圍；影像兩個預設不勾、要另外確認（SPEC #api-keys-media）。 */
export const API_KEY_SCOPES = [
  "devices.read",
  "events.read",
  "recordings.read",
  "alarms.handle",
  "media.snapshot.read",
  "media.clip.read",
  "devices.console",
  "devices.control",
  "devices.maintain",
  "vms.organize",
  "pairing.allocate",
] as const;
/** 建立 / 修改範圍頁的分組與順序：讀取 → 影像 → 寫入（每個範圍一行，要確認的緊跟在下面）。每個 API_KEY_SCOPES 都要在某一組（測試擋）。 */
export const API_KEY_SCOPE_GROUPS: { key: "read" | "media" | "write"; scopes: string[] }[] = [
  { key: "read", scopes: ["devices.read", "events.read", "recordings.read"] },
  { key: "media", scopes: ["media.snapshot.read", "media.clip.read"] },
  { key: "write", scopes: ["alarms.handle", "vms.organize", "devices.control", "devices.maintain", "devices.console", "pairing.allocate"] },
];
/** Kinio Node 區網直連 token（SPEC #node-ttl-direct）：預設不勾、要另外確認（能讀寫接在 Node 上的設備的序列埠）。 */
export const API_KEY_CONSOLE_SCOPE = "devices.console";
/** 對設備下命令 / 改設定（SPEC #api-keys-control，plan#79）：預設不勾、要另外確認（Claude 可以用我的身分控制設備）。 */
export const API_KEY_CONTROL_SCOPE = "devices.control";
/** 改名、OTA（SPEC #api-keys-scopes，Eddy 2026-10-19）：預設不勾、要另外確認。 */
export const API_KEY_MAINTAIN_SCOPE = "devices.maintain";
/** 配網時配發 AID（SPEC #api-keys-aid-selfserve，plan#84）：只在 dev、預設不勾、要另外確認。 */
export const API_KEY_PAIRING_SCOPE = "pairing.allocate";
export const API_KEY_DEFAULT_SCOPES = ["devices.read", "events.read"];
export const API_KEY_MEDIA_SCOPES = ["media.snapshot.read", "media.clip.read"];

export function apiKeyActive(k: ApiKeyInfo, now = Date.now()): boolean {
  return !k.revoked_at && new Date(k.expires_at).getTime() > now;
}

/** 已輪換、還在 24 小時寬限內（還能用；只能撤銷、不算在上限裡，plan SPEC #api-keys-lifecycle）。 */
export function apiKeyRotated(k: ApiKeyInfo, now = Date.now()): boolean {
  return apiKeyActive(k, now) && !!k.replaced_by;
}

export async function listApiKeys(
  fetchImpl: typeof fetch = fetch,
): Promise<{ keys: ApiKeyInfo[]; limit: number }> {
  const r = await bff<{ api_keys: ApiKeyInfo[]; limit: number }>(
    "/session/api-keys",
    {},
    session.accessToken(),
    fetchImpl,
  );
  return { keys: r.api_keys ?? [], limit: r.limit ?? 5 };
}

/** 建金鑰（要 5 分鐘內驗證過，會自動跳重新驗證）；secret 只在這次回應。 */
export function createApiKey(
  input: {
    name: string;
    scopes: string[];
    expires_in_days: number;
    restrictions?: ApiKeyInfo["restrictions"];
  },
  fetchImpl: typeof fetch = fetch,
): Promise<{ api_key: ApiKeyInfo; secret: string }> {
  return withStepUp(() =>
    bff<{ api_key: ApiKeyInfo; secret: string }>(
      "/session/api-keys/create",
      input,
      session.accessToken(),
      fetchImpl,
    ),
  );
}

export function revokeApiKey(
  keyId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  return withStepUp(() =>
    bff(
      "/session/api-keys/revoke",
      { key_id: keyId },
      session.accessToken(),
      fetchImpl,
    ),
  );
}

/** 輪換（要 5 分鐘內驗證過，會自動跳重新驗證）：同設定建新的，新 secret 只在這次回應；舊的 24 小時後失效。 */
export function rotateApiKey(
  keyId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ api_key: ApiKeyInfo; secret: string; replaced: ApiKeyInfo }> {
  return withStepUp(() =>
    bff<{ api_key: ApiKeyInfo; secret: string; replaced: ApiKeyInfo }>(
      "/session/api-keys/rotate",
      { key_id: keyId },
      session.accessToken(),
      fetchImpl,
    ),
  );
}

/** 改範圍（plan SPEC #api-keys-edit-scopes；要 5 分鐘內驗證過，會自動跳重新驗證）：secret 不變，Claude 不用重設。 */
export function updateApiKeyScopes(
  keyId: string,
  scopes: string[],
  fetchImpl: typeof fetch = fetch,
): Promise<{ api_key: ApiKeyInfo }> {
  return withStepUp(() =>
    bff<{ api_key: ApiKeyInfo }>(
      "/session/api-keys/scopes",
      { key_id: keyId, scopes },
      session.accessToken(),
      fetchImpl,
    ),
  );
}

export function revokeAllApiKeys(
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  return withStepUp(() =>
    bff("/session/api-keys/revoke-all", {}, session.accessToken(), fetchImpl),
  );
}

// ---- App 帳號連結（plan SPEC #account-split-app-link）：在 VMS 確認開發者入口產生的 8 碼 ----

/** 連結種類：self = 本人連結（預設）；grant = 跨人 / lab 額度授權連結（只開 allocate_aid，plan SPEC #account-split-app-link）。 */
export type AppLinkKind = 'self' | 'grant';

export interface AppLinkInfo {
  link_id: string;
  portal_email: string;
  portal_display_name?: string;
  kind?: AppLinkKind;
  created_at: string;
}

export interface AppLinkPreview {
  portal_email: string;
  portal_display_name?: string;
  app_realm: string;
  kind?: AppLinkKind;
  expires_at: string;
}

export async function listAppLinks(fetchImpl: typeof fetch = fetch): Promise<AppLinkInfo[]> {
  const r = await bff<{ links: AppLinkInfo[] }>('/session/app-links', {}, session.accessToken(), fetchImpl);
  return r.links ?? [];
}

/** 寄 email 驗證信（新增登入方式時 403 EMAIL_NOT_VERIFIED 的〔寄驗證信〕，kq Q143）。回 false = 其實已經驗證過（identity 200 verified:true，不寄）。 */
export async function sendEmailVerification(fetchImpl: typeof fetch = fetch): Promise<boolean> {
  const r = await bff<{ sent?: boolean; verified?: boolean }>('/session/email/verify-send', {}, session.accessToken(), fetchImpl);
  return !r?.verified;
}

/** 預覽連結碼（沒有副作用）：是哪個開發者帳號要連。 */
export function previewAppLink(code: string, fetchImpl: typeof fetch = fetch): Promise<AppLinkPreview> {
  return bff<AppLinkPreview>('/session/app-links/preview', { code }, session.accessToken(), fetchImpl);
}

/** 確認連結（要 5 分鐘內驗證過，會自動跳重新驗證）；成功後馬上換 token（refresh_recommended，SPEC 走查 W-02）。 */
export async function confirmAppLink(code: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  await withStepUp(() => bff('/session/app-links/confirm', { code }, session.accessToken(), fetchImpl));
  await restore(fetchImpl);
}

/** 解除連結（任一邊都可以）；解除後同樣換 token。 */
export async function deleteAppLink(linkId: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  await bff('/session/app-links/delete', { link_id: linkId }, session.accessToken(), fetchImpl);
  await restore(fetchImpl);
}

// ---- 企業 SSO（plan SPEC #vms-web-sso-detail、#vms-web-sso-ui，cloud/platform#64）----
// IdP 來回都經 vms-web：begin 回 IdP 網址（整頁跳轉）；IdP → identity → /sso/done#code=…&r=… → exchange。

export interface SsoDiscovery {
  org_id: string;
  org_display_name: string;
  idp_display_name: string;
}

/** 登入框 home realm discovery：email 網域是某組織驗證過的網域、而且組織有啟用的 IdP → 那個組織；否則 null。 */
export async function ssoDiscover(email: string, fetchImpl: typeof fetch = fetch): Promise<SsoDiscovery | null> {
  if (!email.includes('@')) return null;
  try {
    const r = await bff<{ sso: SsoDiscovery | null }>('/session/sso/discover', { email: email.trim() }, null, fetchImpl);
    return r?.sso ?? null;
  } catch {
    return null; // 查不到就退回密碼登入
  }
}

/** 用組織 id 查（/sso/start、需要以公司帳號驗證的中介頁）。 */
export async function ssoDiscoverOrg(orgId: string, fetchImpl: typeof fetch = fetch): Promise<SsoDiscovery | null> {
  try {
    const r = await bff<{ sso: SsoDiscovery | null }>('/session/sso/discover', { org_id: orgId }, null, fetchImpl);
    return r?.sso ?? null;
  } catch {
    return null;
  }
}

export type SsoMode = 'login' | 'step_up' | 'link' | 'test';

/** 開始一次 SSO：回 IdP 網址（呼叫端整頁跳轉）。login 不帶 token；其他帶目前的 access token。 */
export async function ssoBegin(orgId: string, mode: SsoMode, returnPath: string, fetchImpl: typeof fetch = fetch): Promise<string> {
  const r = await bff<{ authorize_url: string }>('/session/sso/begin', { org_id: orgId, mode, return_path: returnPath }, mode === 'login' ? null : session.accessToken(), fetchImpl);
  return r.authorize_url;
}

export interface SsoExchangeResult {
  return_path: string;
  /** step-up / link：帳號 email 是公司驗證過的網域 → 提醒換成個人信箱 */
  email_reminder?: boolean;
  tested?: boolean;
  tested_version?: number;
}

/**
 * /sso/done 拿到交換碼：login → 建立 session；step_up / link / test → 用目前的工作階段交換（整頁跳轉回來時記憶體裡沒有 token，先 restore）。
 */
export async function ssoExchange(code: string, ref: string, fetchImpl: typeof fetch = fetch): Promise<SsoExchangeResult> {
  if (!session.accessToken()) await restore(fetchImpl).catch(() => null);
  const r = await bff<SsoExchangeResult & { token?: BffLogin }>('/session/sso/exchange', { code, r: ref }, session.accessToken(), fetchImpl);
  if (r.token?.refresh_token) adoptLogin(r.token);
  else if (r.token?.access_token) adoptAccessToken(r.token.access_token, r.token.expires_in);
  return { return_path: r.return_path || '/', email_reminder: r.email_reminder, tested: r.tested, tested_version: r.tested_version };
}

/** break-glass：先用 passkey 重新驗證，再取得這個組織的緊急證明（plan SPEC #vms-web-sso-break-glass）。 */
export async function ssoBreakGlass(orgId: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  const r = await bff<{ token: { access_token: string; expires_in?: number } }>('/session/sso/break-glass', { org_id: orgId }, session.accessToken(), fetchImpl);
  adoptAccessToken(r.token.access_token, r.token.expires_in);
}

export interface OrgIdP {
  idp_id: string;
  protocol: 'oidc' | 'saml';
  display_name: string;
  status: 'draft' | 'active' | 'disabled';
  config: Record<string, unknown>;
  pending_config?: Record<string, unknown>;
  version: number;
  tested_version?: number;
  has_client_secret: boolean;
  sp: Record<string, string>;
}

export interface OrgDomain {
  domain: string;
  status: 'pending' | 'verified';
  txt_name: string;
  txt_value: string;
  verified_at?: string;
}

export function orgIdPGet(orgId: string, fetchImpl: typeof fetch = fetch): Promise<{ idp: OrgIdP | null }> {
  return bff('/session/sso/org/' + encodeURIComponent(orgId) + '/idp', {}, session.accessToken(), fetchImpl);
}

/** 建 / 改 IdP（寫入要 5 分鐘內驗證過：REAUTH_REQUIRED → 重新驗證再送）。 */
export function orgIdPPut(orgId: string, body: Record<string, unknown>, fetchImpl: typeof fetch = fetch): Promise<{ idp: OrgIdP }> {
  return withStepUp(() => bff('/session/sso/org/' + encodeURIComponent(orgId) + '/idp', body, session.accessToken(), fetchImpl));
}

export function orgIdPDelete(orgId: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  return withStepUp(() => bff('/session/sso/org/' + encodeURIComponent(orgId) + '/idp', { _delete: true }, session.accessToken(), fetchImpl));
}

export function orgDomains(orgId: string, fetchImpl: typeof fetch = fetch): Promise<{ domains: OrgDomain[] }> {
  return bff('/session/sso/org/' + encodeURIComponent(orgId) + '/domains', {}, session.accessToken(), fetchImpl);
}

export function orgDomainAdd(orgId: string, domain: string, fetchImpl: typeof fetch = fetch): Promise<{ domain: OrgDomain }> {
  return withStepUp(() => bff('/session/sso/org/' + encodeURIComponent(orgId) + '/domains', { domain }, session.accessToken(), fetchImpl));
}

export function orgDomainVerify(orgId: string, domain: string, fetchImpl: typeof fetch = fetch): Promise<{ domain: OrgDomain }> {
  return withStepUp(() => bff('/session/sso/org/' + encodeURIComponent(orgId) + '/domain-verify', { domain }, session.accessToken(), fetchImpl));
}

export function orgDomainDelete(orgId: string, domain: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  return withStepUp(() => bff('/session/sso/org/' + encodeURIComponent(orgId) + '/domain-delete', { domain }, session.accessToken(), fetchImpl));
}

export interface SsoLink {
  idp_id: string;
  org_id: string;
  org_display_name: string;
  idp_display_name: string;
  linked_at: string;
}

export async function ssoLinks(fetchImpl: typeof fetch = fetch): Promise<SsoLink[]> {
  const r = await bff<{ sso_links: SsoLink[] }>('/session/sso/links', {}, session.accessToken(), fetchImpl);
  return r?.sso_links ?? [];
}

export function ssoUnlink(idpId: string, fetchImpl: typeof fetch = fetch): Promise<void> {
  return withStepUp(() => bff('/session/sso/unlink', { idp_id: idpId }, session.accessToken(), fetchImpl));
}
